// ShowerGlassTypesSection.jsx
// Showers division (08 00 02) - Glass Types tab

import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Trash2, Edit2, Eye, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { NumericField } from '../../components/NumericField';
import { getNumericFieldError } from '../../utils/numericValidation';

const GLASS_FEATURES = [
  'Tempered', 'Laminated', 'Digital Printed',
  'Low Iron', 'Heat Soaked', 'Frosted'
];

const COMMON_GLASS_THICKNESSES = [
  { value: 0.125, label: '1/8" (3mm)' },
  { value: 0.1875, label: '3/16" (5mm)' },
  { value: 0.25, label: '1/4" (6mm)' },
  { value: 0.375, label: '3/8" (10mm)' },
  { value: 0.5, label: '1/2" (12mm)' },
  { value: 0.625, label: '5/8" (16mm)' },
  { value: 0.75, label: '3/4" (19mm)' },
  { value: 1, label: '1" (25mm)' },
];

export const ShowerGlassTypesSection = ({
  filteredShowerGlassTypes,
  selectedShowerGlassTypeIds,
  expandedShowerGlassTypeIds,
  canEditCSIItems,
  canManageProject,
  operationLoading,
  showShowerGlassTypeDialog,
  editingShowerGlassType,
  newShowerGlassType,
  onSelectAll,
  onConfirmBulkDelete,
  onToggleSelection,
  onDialogOpenChange,
  onFormChange,
  onFormSubmit,
  onEdit,
  onDelete,
  onToggleExpand,
}) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const warrantyRules = { integer: true, gtZero: true, label: 'Warranty' };
  const handleSubmit = (e) => {
    setSubmitAttempted(true);
    if (getNumericFieldError(newShowerGlassType.warrantyYears, warrantyRules)) {
      e.preventDefault();
      return;
    }
    onFormSubmit(e);
  };

  return (
    <div data-testid="shower-glass-types-section">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Glass Types</h2>
        <div className="flex gap-2">
          {canEditCSIItems && filteredShowerGlassTypes.length > 0 && (
            <>
              <Button variant="outline" onClick={onSelectAll}>
                {selectedShowerGlassTypeIds.length === filteredShowerGlassTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                onClick={onConfirmBulkDelete}
                disabled={selectedShowerGlassTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedShowerGlassTypeIds.length})
              </Button>
            </>
          )}
          {canEditCSIItems && (
            <Dialog open={showShowerGlassTypeDialog} onOpenChange={(open) => { setSubmitAttempted(false); onDialogOpenChange(open); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-shower-glass-type-btn" disabled={operationLoading}>
                  {operationLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Add Glass Type
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingShowerGlassType ? 'Edit Glass Type' : 'Add Glass Type'}</DialogTitle>
                  <DialogDescription>
                    Define glass specifications for shower and door glass lites
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  {/* Glass Information */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm border-b pb-2">Glass Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Glass Type *</Label>
                        <Input
                          data-testid="shower-glass-type-name"
                          placeholder="e.g., GT1, GT2, GT3"
                          value={newShowerGlassType.name}
                          onChange={(e) => onFormChange({ ...newShowerGlassType, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Glass Manufacturer</Label>
                        <Input
                          placeholder="e.g., Guardian, PPG, Cardinal"
                          value={newShowerGlassType.manufacturer || ''}
                          onChange={(e) => onFormChange({ ...newShowerGlassType, manufacturer: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Glass Build</Label>
                        <Input
                          placeholder="e.g., 1/4 + 1/2 Air + 1/4"
                          value={newShowerGlassType.glassBuild || ''}
                          onChange={(e) => onFormChange({ ...newShowerGlassType, glassBuild: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Glass Thickness (in)</Label>
                        <Select
                          value={newShowerGlassType.glassThickness?.toString() || ''}
                          onValueChange={(val) => onFormChange({ ...newShowerGlassType, glassThickness: parseFloat(val) })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select thickness" />
                          </SelectTrigger>
                          <SelectContent>
                            {COMMON_GLASS_THICKNESSES.map((t) => (
                              <SelectItem key={t.value} value={t.value.toString()}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Glass Features */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm border-b pb-2">Glass Features</h4>
                    <div className="space-y-2">
                      <Label>Color</Label>
                      <Input
                        placeholder="e.g., Clear, Bronze, Gray"
                        value={newShowerGlassType.color || ''}
                        onChange={(e) => onFormChange({ ...newShowerGlassType, color: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 border rounded-md">
                      {GLASS_FEATURES.map((feature) => (
                        <label key={feature} className="flex items-center space-x-2 cursor-pointer">
                          <Checkbox
                            checked={(newShowerGlassType.features || []).includes(feature)}
                            onCheckedChange={(checked) => {
                              const current = newShowerGlassType.features || [];
                              onFormChange({
                                ...newShowerGlassType,
                                features: checked ? [...current, feature] : current.filter(f => f !== feature)
                              });
                            }}
                          />
                          <span className="text-sm font-normal">{feature}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Warranty */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm border-b pb-2">Warranty</h4>
                    <div className="space-y-2">
                      <Label>Warranty Period (Years)</Label>
                      <NumericField
                        placeholder="e.g., 10"
                        value={newShowerGlassType.warrantyYears}
                        onChange={(v) => onFormChange({ ...newShowerGlassType, warrantyYears: v })}
                        rules={warrantyRules}
                        showError={submitAttempted}
                        data-testid="shower-glass-warranty-input"
                        errorTestId="shower-glass-warranty-error"
                      />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onDialogOpenChange(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={operationLoading} data-testid="save-shower-glass-type-btn">
                      {operationLoading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{editingShowerGlassType ? 'Saving...' : 'Adding...'}</>
                      ) : (
                        editingShowerGlassType ? 'Save Changes' : 'Add Glass Type'
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* List */}
      {filteredShowerGlassTypes.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg">
          <p>No glass types created yet</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-slate-100">
              <tr className="border-b">
                {canEditCSIItems && (
                  <th className="w-12 px-3 py-3 text-left">
                    <Checkbox
                      checked={selectedShowerGlassTypeIds.length === filteredShowerGlassTypes.length && filteredShowerGlassTypes.length > 0}
                      onCheckedChange={onSelectAll}
                    />
                  </th>
                )}
                <th className="w-24 px-3 py-3 text-left font-semibold text-slate-700">Type</th>
                <th className="w-36 px-3 py-3 text-left font-semibold text-slate-700">Manufacturer</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Build</th>
                <th className="w-28 px-3 py-3 text-left font-semibold text-slate-700">Thickness</th>
                <th className="w-24 px-3 py-3 text-left font-semibold text-slate-700">Color</th>
                <th className="w-28 px-3 py-3 text-left font-semibold text-slate-700">Warranty</th>
                {canEditCSIItems && (
                  <th className="w-28 px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredShowerGlassTypes.map((gt, idx) => (
                <React.Fragment key={gt.id}>
                  <tr
                    className={`border-b hover:bg-slate-50 transition-colors ${
                      selectedShowerGlassTypeIds.includes(gt.id) ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    }`}
                    data-testid={`shower-glass-type-${gt.id}`}
                  >
                    {canEditCSIItems && (
                      <td className="w-12 px-3 py-2">
                        <Checkbox
                          checked={selectedShowerGlassTypeIds.includes(gt.id)}
                          onCheckedChange={() => onToggleSelection(gt.id, null)}
                        />
                      </td>
                    )}
                    <td className="w-24 px-3 py-2 font-medium text-slate-900">{gt.name}</td>
                    <td className="w-36 px-3 py-2 text-slate-600 truncate">{gt.manufacturer || '-'}</td>
                    <td className="px-3 py-2 text-slate-600 truncate">{gt.glassBuild || '-'}</td>
                    <td className="w-28 px-3 py-2 text-slate-600">{gt.glassThickness ? `${gt.glassThickness}"` : '-'}</td>
                    <td className="w-24 px-3 py-2 text-slate-600">{gt.color || '-'}</td>
                    <td className="w-28 px-3 py-2 text-slate-600">{gt.warrantyYears ? `${gt.warrantyYears} Yrs` : '-'}</td>
                    {canEditCSIItems && (
                      <td className="w-28 px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-7 w-7 p-0 transition-colors ${
                              expandedShowerGlassTypeIds?.includes(gt.id)
                                ? 'bg-orange-500 text-white hover:bg-orange-600'
                                : 'text-slate-500 hover:bg-orange-500 hover:text-white'
                            }`}
                            onClick={() => onToggleExpand(gt.id)}
                            data-testid={`expand-shower-glass-${gt.id}`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(gt)} data-testid={`edit-shower-glass-${gt.id}`}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white" onClick={() => onDelete(gt)} data-testid={`delete-shower-glass-${gt.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {/* Expanded Details */}
                  {expandedShowerGlassTypeIds?.includes(gt.id) && (
                    <tr className="bg-slate-50 border-b">
                      {canEditCSIItems && <td className="w-12"></td>}
                      <td colSpan={canEditCSIItems ? 7 : 6} className="px-3 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                          {gt.features && gt.features.length > 0 && (
                            <div className="col-span-2 md:col-span-4">
                              <span className="font-semibold text-slate-700">Features:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {gt.features.map((f, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-slate-200 rounded text-xs">{f}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {(!gt.features || gt.features.length === 0) && (
                            <div className="col-span-2 md:col-span-4 text-slate-400 text-xs">No features selected</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ShowerGlassTypesSection;
